import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { MapPin, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import FacebookShare from "./FacebookShare";

function EmberField({ gameId }) {
    const embers = useMemo(
        () =>
            Array.from({ length: 16 }, (_, i) => ({
                left: `${(i * 61 + 13) % 100}%`,
                size: 2 + ((i * 7) % 4),
                delay: `${((i * 0.83) % 6).toFixed(2)}s`,
                duration: `${(5 + ((i * 1.37) % 5)).toFixed(2)}s`,
                sway: `${((i * 37) % 60) - 30}px`,
                amber: i % 3 !== 0,
            })),
        [gameId]
    );
    return (
        <div className="absolute inset-0 overflow-hidden pointer-events-none z-[5]" data-testid={`epic-embers-${gameId}`}>
            {embers.map((e, i) => (
                <span
                    key={i}
                    className="epic-ember absolute bottom-2 rounded-full"
                    style={{
                        left: e.left,
                        width: e.size,
                        height: e.size,
                        background: e.amber ? "#FFB800" : "#FF5500",
                        boxShadow: `0 0 ${e.size * 2}px ${e.amber ? "#FFB800" : "#FF5500"}`,
                        animationDelay: e.delay,
                        animationDuration: e.duration,
                        "--sway": e.sway,
                    }}
                />
            ))}
            <style>{`
                .epic-ember { animation-name: epic-ember-rise; animation-timing-function: linear; animation-iteration-count: infinite; opacity: 0; }
                @keyframes epic-ember-rise {
                    0% { transform: translateY(0) translateX(0); opacity: 0; }
                    12% { opacity: .85; }
                    100% { transform: translateY(-340px) translateX(var(--sway)); opacity: 0; }
                }
            `}</style>
        </div>
    );
}

function shuffleIndices(n) {
    const a = Array.from({ length: n }, (_, i) => i);
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
}

function SideShowcase({ side, color, data, gameId }) {
    const players = data?.players || [];
    const [order, setOrder] = useState([]);
    const [idx, setIdx] = useState(0);

    useEffect(() => {
        setOrder(shuffleIndices(players.length));
        setIdx(0);
    }, [players.length, gameId]);

    useEffect(() => {
        if (order.length < 2) return undefined;
        const t = setInterval(() => setIdx((i) => (i + 1) % order.length), 5000);
        return () => clearInterval(t);
    }, [order.length]);

    const current = order.length ? players[order[idx]] : null;
    const [shown, setShown] = useState(null);

    useEffect(() => {
        if (!current) {
            setShown(null);
            return undefined;
        }
        let cancelled = false;
        const pre = new Image();
        pre.onload = () => !cancelled && setShown(current);
        pre.onerror = () => !cancelled && setShown(current);
        pre.src = current.url;
        return () => {
            cancelled = true;
        };
    }, [current && current.url]);

    const logoSrc = data?.logo_art || data?.logo;
    if (!current && !logoSrc) return null;

    return (
        <div
            data-testid={`epic-showcase-${side}-${gameId}`}
            className={`absolute bottom-10 h-[84%] w-[56%] pointer-events-none ${
                side === "left" ? "-left-2 sm:left-0" : "-right-2 sm:right-0"
            }`}
        >
            {shown && (
                <img
                    key={shown.url}
                    src={shown.url}
                    alt={shown.name}
                    data-testid={`epic-player-${side}-${gameId}`}
                    className="absolute inset-0 h-full w-full epic-player-fade opacity-90 object-cover object-top"
                    style={{ filter: `drop-shadow(0 0 18px ${color}55) brightness(0.92)` }}
                />
            )}
            {shown && (
                <div
                    key={`cap-${shown.url}`}
                    data-testid={`epic-caption-${side}-${gameId}`}
                    className="epic-caption absolute inset-x-0 bottom-24 sm:bottom-28 flex justify-center pointer-events-none"
                >
                    <span
                        className="inline-flex items-center gap-1.5 -skew-x-6 bg-[#08080C]/80 px-3 py-1 border-l-4 font-display font-black uppercase italic text-[10px] sm:text-xs tracking-[0.12em] whitespace-nowrap"
                        style={{ borderColor: color, boxShadow: `0 0 14px ${color}55`, opacity: 0.8 }}
                    >
                        {shown.number != null && (
                            <span className="font-stat font-black" style={{ color }}>
                                #{shown.number}
                            </span>
                        )}
                        {shown.name}
                    </span>
                </div>
            )}
            {logoSrc && (
                <img
                    src={logoSrc}
                    alt={`${side === "left" ? "Home" : "Away"} team emblem`}
                    data-testid={`epic-logo-${side}-${gameId}`}
                    className={`absolute top-1 w-14 h-14 sm:w-20 sm:h-20 object-contain epic-logo-pulse ${
                        side === "left" ? "left-1 sm:left-2" : "right-1 sm:right-2"
                    }`}
                    style={{ filter: `drop-shadow(0 0 22px ${color}B3)` }}
                />
            )}
            <StaffPics staff={data?.staff} color={color} side={side} gameId={gameId} />
            <style>{`
                .epic-player-fade { animation: epic-player-in 0.9s ease-out; }
                @keyframes epic-player-in { from { opacity: 0; transform: translateY(10px) scale(0.98); } to { opacity: .9; transform: none; } }
                .epic-logo-pulse { animation: epic-logo-glow 3.2s ease-in-out infinite; }
                @keyframes epic-logo-glow { 0%,100% { transform: scale(1); } 50% { transform: scale(1.05); } }
                .epic-caption { animation: epic-caption-cycle 5s ease-in-out; }
                @keyframes epic-caption-cycle { 0% { opacity: 0; transform: translateY(8px); } 12% { opacity: 1; transform: none; } 82% { opacity: 1; } 100% { opacity: 0; transform: translateY(-4px); } }
            `}</style>
        </div>
    );
}

function CrestBadge({ team, color, testid }) {
    if (team?.logo) {
        return (
            <img
                src={team.logo}
                alt={team.short_name || team.name || "Team crest"}
                data-testid={testid}
                className="w-5 h-5 sm:w-6 sm:h-6 object-contain not-italic"
                style={{ filter: `drop-shadow(0 0 8px ${color})` }}
            />
        );
    }
    return (
        <span
            data-testid={testid}
            className="w-5 h-5 sm:w-6 sm:h-6 rounded flex items-center justify-center font-display font-black text-[8px] sm:text-[9px] not-italic"
            style={{ background: `${color}26`, border: `1px solid ${color}80`, color, textShadow: `0 0 8px ${color}` }}
        >
            {(team?.short_name || team?.name || "??").slice(0, 3).toUpperCase()}
        </span>
    );
}

function StaffPics({ staff, color, side, gameId }) {
    const pics = (staff || []).filter((s) => s.url).slice(0, 3);
    if (!pics.length) return null;
    return (
        <div
            data-testid={`epic-staff-${side}-${gameId}`}
            className={`absolute bottom-0 z-[6] flex items-end pointer-events-none ${
                side === "left" ? "left-1 -space-x-2" : "right-1 -space-x-2 flex-row-reverse"
            }`}
        >
            {pics.map((s, i) => (
                <img
                    key={`${s.name}-${i}`}
                    src={s.url}
                    alt={`${s.name} (${s.role})`}
                    title={`${s.name} — ${s.role}`}
                    data-testid={`epic-staff-${side}-${gameId}-${i}`}
                    className="h-20 sm:h-28 w-auto object-contain object-bottom rounded-lg epic-player-fade"
                    style={{ filter: `drop-shadow(0 0 12px ${color}80) brightness(0.95)` }}
                />
            ))}
        </div>
    );
}

function LightningVS({ gameId }) {
    const gradId = `boltGrad-${gameId}`;
    return (
        <div
            data-testid={`epic-vs-${gameId}`}
            className="absolute top-[30%] left-1/2 -translate-x-1/2 -translate-y-1/2 z-10 pointer-events-none flex items-center justify-center"
        >
            <svg
                className="absolute w-16 h-28 sm:w-20 sm:h-36 epic-bolt-path"
                viewBox="0 0 100 160"
                fill="none"
                aria-hidden="true"
            >
                <defs>
                    <linearGradient id={gradId} x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stopColor="#FFE08A" />
                        <stop offset="50%" stopColor="#FFB800" />
                        <stop offset="100%" stopColor="#FF5500" />
                    </linearGradient>
                </defs>
                <path
                    d="M58 0 L26 72 L46 72 L22 160 L78 52 L52 52 L74 0 Z"
                    fill={`url(#${gradId})`}
                    fillOpacity="0.35"
                    stroke={`url(#${gradId})`}
                    strokeWidth="5"
                    strokeLinejoin="round"
                    className="epic-bolt-strike"
                    pathLength="100"
                />
            </svg>
            <span
                className="relative z-10 font-display font-black text-4xl sm:text-6xl bg-gradient-to-b from-white via-[#FFD34D] to-[#FF5500] bg-clip-text text-transparent epic-vs-text"
                style={{ WebkitTextStroke: "1.5px rgba(8,8,12,0.85)" }}
            >
                VS
            </span>
            <style>{`
                .epic-bolt-path { filter: drop-shadow(0 0 10px #FFB800) drop-shadow(0 0 30px #FF5500); animation: epic-bolt-flicker 2.6s infinite; }
                .epic-bolt-strike { stroke-dasharray: 100; stroke-dashoffset: 100; animation: epic-bolt-strike 2.6s infinite; }
                .epic-vs-text { animation: epic-vs-pulse 2.6s infinite; }
                @keyframes epic-bolt-strike { 0% { stroke-dashoffset: 100 } 18% { stroke-dashoffset: 0 } 100% { stroke-dashoffset: 0 } }
                @keyframes epic-bolt-flicker { 0%,14% { opacity: 0 } 18% { opacity: 1 } 22% { opacity: .3 } 26% { opacity: 1 } 30% { opacity: .5 } 34%,88% { opacity: .95 } 94% { opacity: .2 } 100% { opacity: 0 } }
                @keyframes epic-vs-pulse { 0%,100% { filter: drop-shadow(0 0 12px rgba(255,184,0,.5)) } 20% { filter: drop-shadow(0 0 34px rgba(255,85,0,.95)) } 50% { filter: drop-shadow(0 0 18px rgba(255,184,0,.65)) } }
            `}</style>
        </div>
    );
}

export default function EpicMatchup({ game }) {
    const homeColor = game.home_team?.primary_color || "#FF5500";
    const awayColor = game.away_team?.primary_color || "#A855F7";
    const [card, setCard] = useState({ headline: null, home: null, away: null });

    useEffect(() => {
        let cancelled = false;
        const load = () => {
            api.get(`/games/${game.id}/epic-teams`)
                .then((r) => !cancelled && setCard(r.data))
                .catch(() => {});
        };
        load();
        const t = setInterval(load, 12000);
        return () => {
            cancelled = true;
            clearInterval(t);
        };
    }, [game.id]);

    const rendering = [card.home, card.away].some((s) => !s || (!s.players?.length && !s.logo));

    return (
        <div
            data-testid={`epic-matchup-${game.id}`}
            className="relative rounded-3xl overflow-hidden border border-[#262638] h-80 sm:h-96 group"
        >
            <div className="absolute top-3 right-3 z-30">
                <FacebookShare
                    path={`/share/game/${game.id}`}
                    target={`epic-matchup-${game.id}`}
                    fileName={`ykpbl-matchup-${game.id}.png`}
                    testid={`share-game-${game.id}`}
                />
            </div>
            <img
                src="/battle.jpg"
                alt=""
                className="absolute inset-0 w-full h-full object-cover opacity-40 transition-transform duration-700 group-hover:scale-105"
            />
            <div
                className="absolute inset-0"
                style={{
                    background: `linear-gradient(105deg, ${homeColor}40 0%, transparent 60%)`,
                    clipPath: "polygon(0 0, 62% 0, 38% 100%, 0 100%)",
                }}
            />
            <div
                className="absolute inset-0"
                style={{
                    background: `linear-gradient(255deg, ${awayColor}40 0%, transparent 60%)`,
                    clipPath: "polygon(62% 0, 100% 0, 100% 100%, 38% 100%)",
                }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#08080C] via-transparent to-[#08080C]/60" />
            <div className="absolute inset-y-0 left-1/2 w-px -translate-x-1/2 rotate-12 bg-gradient-to-b from-transparent via-[#FFB800]/60 to-transparent" />

            <EmberField gameId={game.id} />

            {game.week != null && (
                <span className="absolute top-4 right-4 text-[10px] font-stat text-slate-300 bg-[#08080C]/70 px-2.5 py-1.5 rounded-full border border-[#262638] z-20">
                    Week {game.week}
                </span>
            )}

            <SideShowcase side="left" color={homeColor} data={card.home} gameId={game.id} />
            <SideShowcase side="right" color={awayColor} data={card.away} gameId={game.id} />

            <LightningVS gameId={game.id} />

            {rendering && (
                <p
                    data-testid={`epic-poster-loading-${game.id}`}
                    className="absolute top-1/2 left-1/2 -translate-x-1/2 z-10 flex items-center gap-1.5 text-[10px] font-display font-bold uppercase tracking-[0.25em] text-slate-400 animate-pulse"
                >
                    <Sparkles className="w-3.5 h-3.5 text-[#FFB800]" /> Preparing the showdown
                </p>
            )}

            <div className="absolute inset-x-0 bottom-12 z-20 text-center px-4 pointer-events-none">
                {card.headline ? (
                    <p
                        data-testid={`epic-headline-${game.id}`}
                        className="font-display font-black uppercase text-2xl sm:text-4xl leading-none tracking-tight bg-gradient-to-b from-white via-[#FFD34D] to-[#FF5500] bg-clip-text text-transparent drop-shadow-[0_2px_18px_rgba(0,0,0,0.9)]"
                    >
                        {card.headline}
                    </p>
                ) : (
                    <p
                        data-testid={`epic-headline-${game.id}`}
                        className="font-display font-black uppercase text-2xl sm:text-4xl leading-none tracking-tight text-white/90 drop-shadow-[0_2px_18px_rgba(0,0,0,0.9)]"
                    >
                        {game.home_team?.short_name || game.home_team?.name} vs{" "}
                        {game.away_team?.short_name || game.away_team?.name}
                    </p>
                )}
                <p
                    data-testid={`epic-teams-${game.id}`}
                    className="mt-2 inline-flex items-center justify-center gap-2 sm:gap-3 font-display font-black uppercase italic -skew-x-3 text-xs sm:text-base tracking-[0.15em] bg-[#08080C]/75 backdrop-blur-sm rounded-full px-4 py-1 border border-white/10"
                >
                    <CrestBadge team={game.home_team} color={homeColor} testid={`epic-crest-home-${game.id}`} />
                    <span
                        className="drop-shadow-[0_2px_8px_rgba(0,0,0,0.9)]"
                        style={{ color: homeColor, textShadow: `0 0 16px ${homeColor}CC, 0 0 42px ${homeColor}66` }}
                    >
                        {game.home_team?.name}
                    </span>
                    <span className="text-stroke-ember text-sm sm:text-lg not-italic">VS</span>
                    <span
                        className="drop-shadow-[0_2px_8px_rgba(0,0,0,0.9)]"
                        style={{ color: awayColor, textShadow: `0 0 16px ${awayColor}CC, 0 0 42px ${awayColor}66` }}
                    >
                        {game.away_team?.name}
                    </span>
                    <CrestBadge team={game.away_team} color={awayColor} testid={`epic-crest-away-${game.id}`} />
                </p>
            </div>

            <div className="absolute bottom-0 inset-x-0 bg-[#08080C]/80 backdrop-blur-sm border-t border-[#262638] px-5 py-3 flex items-center justify-between gap-3 z-20">
                <span className="font-stat text-xs text-[#FF5500]">
                    {format(new Date(game.date), "EEE, MMM d • h:mm a")}
                </span>
                {game.venue && (
                    <span className="flex items-center gap-1.5 text-xs text-slate-400 truncate">
                        <MapPin className="w-3.5 h-3.5 text-[#FFB800] shrink-0" />
                        {game.venue}
                    </span>
                )}
            </div>
        </div>
    );
}
